import { createBrowserRouter, Navigate } from 'react-router';
import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { useAuthStore } from '../store/authStore.js';
import { ErrorBoundary } from '../components/ui/error-boundary.js';
import { PageSkeleton } from '../components/ui/skeleton.js';

// Lazy-loaded feature modules
const LoginPage = lazy(() => import('../features/auth/LoginPage.js'));
const RegisterPage = lazy(() => import('../features/auth/RegisterPage.js'));
const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage.js'));
const AccountsPage = lazy(() => import('../features/accounts/AccountsPage.js'));
const JournalsPage = lazy(() => import('../features/journals/JournalsPage.js'));
const InvoicesPage = lazy(() => import('../features/invoices/InvoicesPage.js'));
const PaymentsPage = lazy(() => import('../features/payments/PaymentsPage.js'));
const ReportsPage = lazy(() => import('../features/reports/ReportsPage.js'));
const BankingPage = lazy(() => import('../features/banking/BankingPage.js'));
const LiabilitiesPage = lazy(() => import('../features/liabilities/LiabilitiesPage.js'));
const TaxPage = lazy(() => import('../features/tax/TaxPage.js'));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage.js'))
const ExpensesPage = lazy(() => import('../features/expenses/ExpensesPage.js'));

function ProtectedRoute({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  // While the refresh-cookie exchange is in flight, hold on the skeleton so we
  // never fire API calls without an access token (and never flash /login only
  // to bounce back here once bootstrap completes).
  if (isBootstrapping) return <PageSkeleton />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Wraps a protected, lazy-loaded page in ErrorBoundary + Suspense + ProtectedRoute */
function P({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute>
      <ErrorBoundary>
        <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

/** Wraps a public lazy-loaded page (login/register) in ErrorBoundary + Suspense */
function PublicPage({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
    </ErrorBoundary>
  );
}


export const router = createBrowserRouter([
  {
    path: '/login',
    element: <PublicPage><LoginPage /></PublicPage>,
  },
  {
    path: '/register',
    element: <PublicPage><RegisterPage /></PublicPage>,
  },
  { path: '/', element: <P><DashboardPage /></P> },
  { path: '/accounts', element: <P><AccountsPage /></P> },
  { path: '/journals', element: <P><JournalsPage /></P> },
  { path: '/invoices', element: <P><InvoicesPage /></P> },
  { path: '/payments', element: <P><PaymentsPage /></P> },
  { path: '/reports', element: <P><ReportsPage /></P> },
  { path: '/banking', element: <P><BankingPage /></P> },
  { path: '/liabilities', element: <P><LiabilitiesPage /></P> },
  { path: '/expenses', element: <P><ExpensesPage /></P> },
  { path: '/tax', element: <P><TaxPage /></P> },
  { path: '/settings', element: <P><SettingsPage /></P> },
  { path: '/fiscal', element: <Navigate to="/settings" replace /> },
  { path: '/admin', element: <Navigate to="/settings" replace /> },
  { path: '/admin/tenants', element: <Navigate to="/settings" replace /> },
  { path: '/admin/tenant-admins', element: <Navigate to="/settings" replace /> },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
